import { useEffect, useState } from 'react';
import type { ApiClient, Raumstand } from '../api';

/**
 * Die Vorbehalte zu den Türanforderungen im Türbuch (Paket TR3).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Türanforderungen (Zutritt, Überwachung, Kamera, Beschlag) leitet die
 * Plattform aus den Schutzzonen der beiden Nachbarräume ab. Ändert sich das
 * Raumbuch, ändern sich die Anforderungen erst mit der nächsten Ableitung –
 * bis dahin zeigte das Türbuch den alten Stand, ohne es zu sagen. In 8000
 * stammte er am 18.10.2026 vom 05.09.2026.
 *
 * Der Server prüft beim Lesen (GET …/raumstand, Rechnung „tueranforderungen")
 * mit einem Probelauf der Ableitung, ob eine Neuableitung etwas ändern würde,
 * und schreibt nichts. Er liefert die Vorbehalte als LISTE (Art, Text,
 * Verweis) – dieselbe, die die Türliste (TUL) druckt: Fachfrage RTA-10 zu R07
 * (immer, solange offen), Raumstand, veraltet.
 *
 * Die Form ist die des TX-Vorbehaltskastens (`muster/Vorbehalte.tsx`,
 * TX-STANDARD 1.6). Nach dem TX-Release wird diese Komponente durch den
 * Aufruf des Kastens mit `stand.vorbehalte` ersetzt.
 *
 * Kann der Stand nicht geladen werden, zeigt die Seite, was sie immer gezeigt
 * hat: Ein fehlender Hinweis ist kein Fehler der Seite.
 */

/** Ein Vorbehalt zu den Türanforderungen (Raumstandpruefung, TR3). */
export interface Tuervorbehalt {
  /** fachfrage | raumstand | veraltet – die Arten des TX-Musters. */
  art: string;
  text: string;
  verweis: string | null;
  /** Das Etikett je Art, vom Server – wie im PDF. */
  etikett: string;
  /** „Text (siehe Verweis)", vom Server – wie im PDF. */
  satz: string;
}

/** Der Raumstand der Rechnung „tueranforderungen" – mit den Vorbehalten. */
export type Tueranforderungsstand = Raumstand & {
  veraltet?: boolean;
  vorbehalte?: Tuervorbehalt[];
};

const TUERANFORDERUNGEN = 'tueranforderungen';

/** Die Überschrift des Kastens – wortgleich mit dem TX-Muster und der TUL. */
const TITEL = 'Vorbehalte zu diesen Ergebnissen';

export function Tueranforderungshinweis({
  api, projektId,
}: {
  api: ApiClient;
  projektId: string;
}) {
  const [vorbehalte, setVorbehalte] = useState<Tuervorbehalt[]>([]);

  useEffect(() => {
    let abgebrochen = false;
    api.raumstand(projektId)
      .then((s) => {
        if (abgebrochen) return;
        const eigener = (s as Tueranforderungsstand[])
          .find((x) => x.rechnung === TUERANFORDERUNGEN);
        setVorbehalte(eigener?.vorbehalte ?? []);
      })
      .catch(() => { if (!abgebrochen) setVorbehalte([]); });
    return () => { abgebrochen = true; };
  }, [api, projektId]);

  if (vorbehalte.length === 0) return null;

  return (
    <div className="hinweis warnung" role="status">
      <div>
        <strong>{TITEL}</strong>
        <ul>
          {vorbehalte.map((v) => (
            <li key={v.art + v.text}>
              <strong>{v.etikett}:</strong> {v.satz}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
